import { clsx } from 'clsx'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

export type ToastKind = 'success' | 'error' | 'info' | 'warning'

interface Toast {
  id: number
  kind: ToastKind
  title: string
  detail?: string
  /** 0 keeps it until dismissed — used for failures worth reading. */
  duration: number
}

interface ToastApi {
  push: (t: { kind?: ToastKind; title: string; detail?: string; duration?: number }) => number
  dismiss: (id: number) => void
  success: (title: string, detail?: string) => number
  error: (title: string, detail?: string) => number
  info: (title: string, detail?: string) => number
}

const Ctx = createContext<ToastApi | null>(null)

export function useToast(): ToastApi {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}

const ICONS: Record<ToastKind, ReactNode> = {
  success: <CheckCircle2 className="h-4 w-4 text-positive" />,
  error: <XCircle className="h-4 w-4 text-negative" />,
  warning: <AlertTriangle className="h-4 w-4 text-caution" />,
  info: <Info className="h-4 w-4 text-info" />,
}

let seq = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([])

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const push = useCallback<ToastApi['push']>(
    ({ kind = 'info', title, detail, duration }) => {
      const id = ++seq
      const ms = duration ?? (kind === 'error' ? 0 : 4200)
      setItems((prev) => [...prev.slice(-4), { id, kind, title, detail, duration: ms }])
      if (ms > 0) window.setTimeout(() => dismiss(id), ms)
      return id
    },
    [dismiss],
  )

  const api = useMemo<ToastApi>(
    () => ({
      push,
      dismiss,
      success: (title, detail) => push({ kind: 'success', title, detail }),
      error: (title, detail) => push({ kind: 'error', title, detail }),
      info: (title, detail) => push({ kind: 'info', title, detail }),
    }),
    [push, dismiss],
  )

  return (
    <Ctx.Provider value={api}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          className="pointer-events-none fixed inset-x-0 bottom-0 z-[200] flex flex-col items-center gap-2 p-4 pb-24 md:items-end md:pb-6"
        >
          {items.map((t) => (
            <div
              key={t.id}
              className={clsx(
                'pointer-events-auto flex w-full max-w-sm items-start gap-2.5 rounded-xl border',
                'bg-surface px-3.5 py-3 shadow-pop animate-slide-up',
                t.kind === 'error' ? 'border-negative/30' : 'border-line',
              )}
            >
              <span className="mt-0.5 shrink-0">{ICONS[t.kind]}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium leading-snug text-ink">{t.title}</p>
                {t.detail ? (
                  <p className="mt-0.5 break-words text-xs leading-relaxed text-ink-3">{t.detail}</p>
                ) : null}
              </div>
              <button
                type="button"
                aria-label="通知を閉じる"
                onClick={() => dismiss(t.id)}
                className="-mr-1 shrink-0 rounded p-1 text-ink-3 hover:bg-surface-hover hover:text-ink"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </Ctx.Provider>
  )
}
